import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { format, isAfter, isSameDay, parseISO, startOfDay } from 'date-fns'
import { Loader2 } from 'lucide-react'
import type { DayButton, Matcher } from 'react-day-picker'
import type { DateRange } from '@/shared/domain'
import { Calendar } from '@/shared/ui/primitives/calendar'
import { Button } from '@/shared/ui/primitives/button'
import { cn } from '@/shared/ui/cn'

export type StyledCalendarDayDecoration = {
  fill?: string
  corner?: { color: string; ariaLabel?: string }
  caption?: ReactNode
  dots?: Array<{ color: string; ariaLabel?: string; hollow?: boolean }>
  ariaLabel?: string
}

export type StyledCalendarLegendItem = {
  type: 'swatch' | 'dot'
  color: string
  label: string
  hollow?: boolean
}

export type StyledCalendarProps = {
  selectedDate?: Date | null
  focusedMonth: Date
  isLoading?: boolean
  onDateSelect: (date: Date) => void
  onMonthChange: (date: Date) => void
  resolveDay?: (date: Date) => StyledCalendarDayDecoration | undefined
  legend?: StyledCalendarLegendItem[]
  minDate?: Date
  rangeStart?: Date | null
  rangeEnd?: Date | null
  enableDragSelect?: boolean
  onRangeSelect?: (range: DateRange) => void
}

function parseDayFromNode(node: Element | null): Date | null {
  const attr = node?.closest('[data-day-iso]')?.getAttribute('data-day-iso')
  if (!attr) return null
  const parsed = parseISO(attr)
  if (Number.isNaN(parsed.getTime())) return null
  return startOfDay(parsed)
}

function normalizeRange(a: Date, b: Date): DateRange {
  if (isSameDay(a, b)) return { start: a, end: b }
  return isAfter(a, b) ? { start: b, end: a } : { start: a, end: b }
}

const DayButtonInner = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof DayButton> & {
    decoration?: StyledCalendarDayDecoration
  }
>(function DayButtonInner(
  { day, modifiers, className, decoration, disabled, ...props },
  ref,
) {
  const isSelected = Boolean(modifiers.selected)
  const isToday = Boolean(modifiers.today)
  const isOutside = Boolean(modifiers.outside)
  const isDisabled = Boolean(disabled)
  const isRangeStart = Boolean(modifiers.range_start)
  const isRangeMiddle = Boolean(modifiers.range_middle)
  const isRangeEnd = Boolean(modifiers.range_end)
  const isInRange = isRangeStart || isRangeMiddle || isRangeEnd

  const rangeFill = isInRange ? 'bg-blaze-soft hover:bg-blaze-soft' : undefined
  const fill =
    decoration?.fill ?? rangeFill ?? 'bg-muted/30 hover:bg-muted/60'
  const rangePosition = isRangeStart
    ? 'start'
    : isRangeEnd
      ? 'end'
      : isRangeMiddle
        ? 'middle'
        : undefined
  const middleBorder =
    isRangeMiddle && decoration?.fill ? 'border-ring/50' : 'border-transparent'

  return (
    <Button
      ref={ref}
      variant="ghost"
      disabled={isDisabled}
      data-day-iso={format(day.date, 'yyyy-MM-dd')}
      data-range-position={rangePosition}
      className={cn(
        'relative flex h-14 w-full flex-col items-center justify-center gap-0.5 rounded-lg border p-0 font-normal leading-none',
        'group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-ring/50',
        fill,
        isSelected || isRangeStart || isRangeEnd
          ? 'border-ring'
          : middleBorder,
        isOutside ? 'opacity-40' : '',
        isDisabled
          ? 'cursor-not-allowed text-muted-foreground/60 opacity-50'
          : '',
        className,
      )}
      {...props}
      {...(decoration?.ariaLabel ? { 'aria-label': decoration.ariaLabel } : {})}
    >
      {decoration?.corner ? (
        <span
          aria-label={decoration.corner.ariaLabel}
          aria-hidden={decoration.corner.ariaLabel ? undefined : true}
          className={cn(
            'pointer-events-none absolute right-1 top-1 size-1.5 rounded-full',
            decoration.corner.color,
          )}
        />
      ) : null}
      <span
        className={cn(
          'text-sm',
          isToday ? 'font-semibold text-primary' : 'font-medium',
        )}
      >
        {format(day.date, 'd')}
      </span>
      {decoration?.caption ? (
        <span className="pointer-events-none flex items-center gap-0.5 text-xs text-muted-foreground">
          {decoration.caption}
        </span>
      ) : null}
      {decoration?.dots && decoration.dots.length > 0 ? (
        <span className="pointer-events-none flex items-center justify-center gap-0.5">
          {decoration.dots.map((dot, idx) => (
            <span
              key={idx}
              aria-label={dot.ariaLabel}
              aria-hidden={dot.ariaLabel ? undefined : true}
              className={cn(
                'size-1 rounded-full',
                dot.hollow && 'border bg-transparent',
                dot.color,
              )}
            />
          ))}
        </span>
      ) : null}
    </Button>
  )
})

function makeDayButton(
  resolveDay?: (date: Date) => StyledCalendarDayDecoration | undefined,
) {
  return function StyledCalendarDayButton(
    props: React.ComponentProps<typeof DayButton>,
  ) {
    const ref = useRef<HTMLButtonElement>(null)
    useEffect(() => {
      if (props.modifiers.focused) ref.current?.focus()
    }, [props.modifiers.focused])

    const decoration = resolveDay?.(props.day.date)
    return <DayButtonInner {...props} ref={ref} decoration={decoration} />
  }
}

export function StyledCalendar({
  selectedDate,
  focusedMonth,
  isLoading = false,
  onDateSelect,
  onMonthChange,
  resolveDay,
  legend,
  minDate,
  rangeStart,
  rangeEnd,
  enableDragSelect = false,
  onRangeSelect,
}: StyledCalendarProps) {
  const MemoizedDayButton = useMemo(
    () => makeDayButton(resolveDay),
    [resolveDay],
  )

  const disabled = useMemo<Matcher | undefined>(
    () => (minDate ? { before: startOfDay(minDate) } : undefined),
    [minDate],
  )

  const [liveRange, setLiveRange] = useState<DateRange | null>(null)
  const liveRangeRef = useRef<DateRange | null>(null)
  const isDraggingRef = useRef<boolean>(false)
  const anchorDateRef = useRef<Date | null>(null)
  const draggedRef = useRef<boolean>(false)
  const suppressClickRef = useRef<boolean>(false)
  const onRangeSelectRef = useRef(onRangeSelect)
  onRangeSelectRef.current = onRangeSelect
  const detachRef = useRef<() => void>(() => undefined)

  const handleDocumentPointerMove = useCallback((e: PointerEvent) => {
    const anchor = anchorDateRef.current
    if (!isDraggingRef.current || !anchor) return
    const day = parseDayFromNode(document.elementFromPoint(e.clientX, e.clientY))
    if (!day) return
    const next = normalizeRange(anchor, day)
    liveRangeRef.current = next
    setLiveRange(next)
    if (!isSameDay(day, anchor)) draggedRef.current = true
  }, [])

  const handleDocumentPointerUp = useCallback(() => {
    if (draggedRef.current && liveRangeRef.current) {
      onRangeSelectRef.current?.(liveRangeRef.current)
      suppressClickRef.current = true
    }
    isDraggingRef.current = false
    anchorDateRef.current = null
    liveRangeRef.current = null
    setLiveRange(null)
    detachRef.current()
  }, [])

  const handleDocumentPointerCancel = useCallback(() => {
    isDraggingRef.current = false
    anchorDateRef.current = null
    draggedRef.current = false
    liveRangeRef.current = null
    setLiveRange(null)
    detachRef.current()
  }, [])

  detachRef.current = () => {
    document.removeEventListener('pointermove', handleDocumentPointerMove)
    document.removeEventListener('pointerup', handleDocumentPointerUp)
    document.removeEventListener('pointercancel', handleDocumentPointerCancel)
  }

  useEffect(() => () => detachRef.current(), [])

  const handleWrapperPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!enableDragSelect) return
    suppressClickRef.current = false
    if (e.pointerType !== 'mouse' && e.pointerType !== 'pen') return
    const day = parseDayFromNode(e.target instanceof Element ? e.target : null)
    if (!day) return
    anchorDateRef.current = day
    isDraggingRef.current = true
    draggedRef.current = false
    liveRangeRef.current = null
    setLiveRange(null)
    document.addEventListener('pointermove', handleDocumentPointerMove)
    document.addEventListener('pointerup', handleDocumentPointerUp)
    document.addEventListener('pointercancel', handleDocumentPointerCancel)
  }

  const handleWrapperClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableDragSelect) return
    if (!suppressClickRef.current) return
    e.preventDefault()
    e.stopPropagation()
    suppressClickRef.current = false
  }

  const modifiers = useMemo<Record<string, Matcher> | undefined>(() => {
    const start = liveRange ? liveRange.start : rangeStart
    const end = liveRange ? liveRange.end : rangeEnd
    if (!start || !end) return undefined
    const sameDay = isSameDay(start, end)
    return {
      range_start: start,
      range_end: end,
      ...(sameDay
        ? {}
        : { range_middle: { after: start, before: end } }),
    }
  }, [liveRange, rangeStart, rangeEnd])

  return (
    <div
      className="relative w-full"
      onPointerDown={handleWrapperPointerDown}
      onClickCapture={handleWrapperClickCapture}
    >
      <Calendar
        mode="single"
        selected={selectedDate ?? undefined}
        month={focusedMonth}
        onSelect={(date) => date && onDateSelect(date)}
        onMonthChange={onMonthChange}
        showOutsideDays
        disabled={disabled}
        modifiers={modifiers}
        className="mx-auto w-full bg-transparent p-0"
        classNames={{
          root: 'w-full',
          months: 'relative flex w-full flex-col gap-3',
          month: 'flex w-full flex-col gap-2',
          month_caption: 'flex h-9 w-full items-center justify-center px-10',
          caption_label: 'text-base font-medium',
          weekdays: 'flex w-full',
          weekday:
            'flex-1 text-xs font-normal text-muted-foreground select-none',
          week: 'mt-1 flex w-full gap-1',
          day: 'group/day relative h-full w-full flex-1 p-0 text-center select-none',
          today: '',
        }}
        components={{ DayButton: MemoizedDayButton }}
      />
      {legend && legend.length > 0 ? (
        <div className="mt-3 flex items-center justify-center gap-6 border-t pt-3">
          {legend.map((item) => (
            <div key={item.label} className="flex items-center gap-1.5">
              <span
                aria-hidden
                className={cn(
                  'inline-block',
                  item.type === 'swatch'
                    ? 'size-3 rounded-sm border border-border'
                    : cn(
                        'size-2 rounded-full',
                        item.hollow && 'border bg-transparent',
                      ),
                  item.color,
                )}
              />
              <span className="text-xs text-muted-foreground">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/60">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : null}
    </div>
  )
}
